import React from 'react';
import { useTranslation } from 'react-i18next';
import type { DraggableGradeBoundariesProps } from '../../types/evaluation';

export const DraggableGradeBoundaries: React.FC<DraggableGradeBoundariesProps> = ({
  gradeSettings,
  products,
  criterionIndex,
  getPosition,
  onBoundaryChange,
  onProductDoubleClick,
  onProductPositionChange
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [draggedBoundaryIndex, setDraggedBoundaryIndex] = React.useState<number | null>(null);
  const [hoveredBoundaryIndex, setHoveredBoundaryIndex] = React.useState<number | null>(null);
  const [isDraggingProduct, setIsDraggingProduct] = React.useState(false);
  const [draggedProductIndex, setDraggedProductIndex] = React.useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const handleMouseDown = (index: number, event: React.MouseEvent) => {
    // index は getBoundaryPairs で設定された実際に変更する等級のインデックス
    // 最下位の等級（D=0など）は境界線を持たないので、最後の境界線のみドラッグ不可にする
    if (index >= gradeSettings.length - 1) return;

    setIsDragging(true);
    setDraggedBoundaryIndex(index);
    event.preventDefault();
  };

  const handleMouseEnter = (index: number) => {
    setHoveredBoundaryIndex(index);
  };

  const handleMouseLeave = () => {
    if (!isDragging) {
      setHoveredBoundaryIndex(null);
    }
  };

  const handleMouseMove = React.useCallback((event: MouseEvent) => {
    if (!isDragging || draggedBoundaryIndex === null || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = ((event.clientX - rect.left) / rect.width) * 100;
    // 境界の制約チェック
    // draggedBoundaryIndexは実際に変更する等級のインデックス（下位等級）
    const minPosition = draggedBoundaryIndex === gradeSettings.length - 1
      ? 0
      : gradeSettings[draggedBoundaryIndex + 1].gradeXStart + 5;
    const maxPosition = draggedBoundaryIndex === 0
      ? 95
      : gradeSettings[draggedBoundaryIndex - 1].gradeXStart - 5;

    const clampedPosition = Math.max(minPosition, Math.min(maxPosition, relativeX));
    onBoundaryChange(draggedBoundaryIndex, Math.round(clampedPosition));
  }, [isDragging, draggedBoundaryIndex, gradeSettings, onBoundaryChange]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
    setDraggedBoundaryIndex(null);
    setHoveredBoundaryIndex(null);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // 製品のドラッグハンドラー
  const handleProductDragStart = (event: React.MouseEvent, productIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingProduct(true);
    setDraggedProductIndex(productIndex);
  };

  const handleProductDragMove = React.useCallback((event: MouseEvent) => {
    if (!isDraggingProduct || draggedProductIndex === null || !containerRef.current || !onProductPositionChange) return;

    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = ((event.clientX - rect.left) / rect.width) * 100;
    const clampedX = Math.max(0, Math.min(100, relativeX));

    // Y位置の計算（重複回避のため）
    const calculateYOffset = (targetX: number, currentIndex: number) => {
      const placedPositions = products
        .map((_, index) => {
          if (index === currentIndex) return null;
          return getPosition(index, criterionIndex);
        })
        .filter((pos): pos is { x: number; y: number } => pos !== null);

      let adjustedY = 0;
      const threshold = 15; // 重複判定の閾値（%）

      const nearbyItems = placedPositions.filter(pos =>
        Math.abs(pos.x - targetX) < threshold
      );

      if (nearbyItems.length > 0) {
        const usedYPositions = nearbyItems.map(pos => pos.y).sort((a, b) => a - b);

        let candidateY = 0;
        while (true) {
          const isTooClose = usedYPositions.some(usedY => Math.abs(candidateY - usedY) < 3);
          if (!isTooClose) {
            adjustedY = candidateY;
            break;
          }
          candidateY += 3;

          if (candidateY > 20) {
            adjustedY = candidateY;
            break;
          }
        }
      }

      return adjustedY;
    };

    const yOffset = calculateYOffset(clampedX, draggedProductIndex);
    onProductPositionChange(draggedProductIndex, clampedX, yOffset);
  }, [isDraggingProduct, draggedProductIndex, products, criterionIndex, getPosition, onProductPositionChange]);

  const handleProductDragEnd = React.useCallback(() => {
    setIsDraggingProduct(false);
    setDraggedProductIndex(null);
  }, []);

  React.useEffect(() => {
    if (isDraggingProduct) {
      document.addEventListener('mousemove', handleProductDragMove);
      document.addEventListener('mouseup', handleProductDragEnd);

      return () => {
        document.removeEventListener('mousemove', handleProductDragMove);
        document.removeEventListener('mouseup', handleProductDragEnd);
      };
    }
  }, [isDraggingProduct, handleProductDragMove, handleProductDragEnd]);

  // 等級境界線のペアを生成（正しい境界位置と名前の対応）
  const getBoundaryPairs = () => {
    const pairs = [];

    // gradeSettingsは降順（A=80, B=60, C=40, D=0）でソートされているとする
    for (let i = 0; i < gradeSettings.length - 1; i++) {
      const upperGrade = gradeSettings[i];       // 上位等級（例：A）
      const lowerGrade = gradeSettings[i + 1];   // 下位等級（例：B）

      // プロパティの安全な取得
      const upperLetter = upperGrade?.gradeLetter || t('gradeAssignment.gradeNumber', { number: i });
      const lowerLetter = lowerGrade?.gradeLetter || t('gradeAssignment.gradeNumber', { number: i + 1 });
      const position = upperGrade?.gradeXStart ?? 0;

      const pair = {
        index: i,
        upperGrade: upperGrade,
        lowerGrade: lowerGrade,
        position: position,
        label: t('gradeAssignment.boundaryLabel', { upper: upperLetter, lower: lowerLetter })
      };

      pairs.push(pair);
    }

    return pairs;
  };

  return (
    <div className="draggable-grade-boundaries">
      <div
        ref={containerRef}
        className="grade-boundary-line-container"
      >
        {/* 評価ラベル */}
        <div className="evaluation-line-labels">
          <span className="evaluation-line-label">{t('comparisonEvaluation.evaluationLine.low')}</span>
          <span className="evaluation-line-label">{t('comparisonEvaluation.evaluationLine.high')}</span>
        </div>
        {/* 背景の数直線 */}
        <div className="evaluation-line__track" />
        {/* 配置済みの評価対象を表示 */}
        {products.map((product, productIndex) => {
          const position = getPosition(productIndex, criterionIndex);
          if (!position) return null;

          // 重複回避の計算
          const calculateYOffset = (targetX: number, currentIndex: number) => {
            const placedPositions = products
              .map((_, index) => {
                if (index === currentIndex) return null;
                return getPosition(index, criterionIndex);
              })
              .filter((pos): pos is { x: number; y: number } => pos !== null);

            let adjustedY = 0;
            const threshold = 15; // 重複判定の閾値（%）

            const nearbyItems = placedPositions.filter(pos =>
              Math.abs(pos.x - targetX) < threshold
            );

            if (nearbyItems.length > 0) {
              const usedYPositions = nearbyItems.map(pos => pos.y).sort((a, b) => a - b);

              let candidateY = 0;
              while (true) {
                const isTooClose = usedYPositions.some(usedY => Math.abs(candidateY - usedY) < 3);
                if (!isTooClose) {
                  adjustedY = candidateY;
                  break;
                }
                candidateY += 3;

                if (candidateY > 50) {
                  adjustedY = candidateY;
                  break;
                }
              }
            }

            return adjustedY;
          };

          const yOffset = calculateYOffset(position.x, productIndex) + 1;
          return (
            <div
              key={productIndex}
              className={`evaluation-line__item evaluation-line__item--small ${isDraggingProduct && draggedProductIndex === productIndex ? 'evaluation-line__item--dragging' : ''}`}
              style={{
                left: `${position.x}%`,
                top: `${70 + yOffset * 18}px`,
                transform: 'translateX(-50%)',
                cursor: isDraggingProduct && draggedProductIndex === productIndex ? 'grabbing' : 'grab'
              }}
              onMouseDown={(e) => handleProductDragStart(e, productIndex)}
              onDoubleClick={() => onProductDoubleClick?.(productIndex)}
              title={t('gradeAssignment.dragOrDoubleClick')}
            >
              <div className="evaluation-line__item-content">
                <span className="evaluation-line__item-title">{product.title || product.reviewTitle}</span>
              </div>
            </div>
          );
        })}
        {/* ドラッグ可能な等級境界線（拡張されたドラッグエリア） */}
        {getBoundaryPairs().map((boundary) => (
          <div
            key={boundary.index}
            className={`grade-boundary-marker ${draggedBoundaryIndex === boundary.index ? 'grade-boundary-marker--dragging' : ''
              } ${hoveredBoundaryIndex === boundary.index ? 'grade-boundary-marker--hovered' : ''
              }`}
            style={{
              left: `${boundary.position}%`,
              top: '25px'
            }}
          >
            {/* 拡張されたドラッグエリア */}
            <div
              className="grade-boundary-marker__drag-area"
              onMouseDown={(e) => handleMouseDown(boundary.index, e)}
              onMouseEnter={() => handleMouseEnter(boundary.index)}
              onMouseLeave={handleMouseLeave}
              style={{
                position: 'absolute',
                left: '-20px',
                right: '-20px',
                top: '-15px',
                height: 'calc(800px + 20rem)',
                cursor: 'ew-resize',
                zIndex: 10
              }}
            />

            <div className="grade-boundary-marker__line" />
            <div className="grade-boundary-marker__handle">
              <div className="grade-boundary-marker__label">
                {t('gradeAssignment.boundaryLabel', { upper: boundary.upperGrade?.gradeLetter || t('gradeAssignment.grade'), lower: boundary.lowerGrade?.gradeLetter || t('gradeAssignment.grade') })}
              </div>
              <div className="grade-boundary-marker__value">
                {Math.round(boundary.position)}%
              </div>
            </div>
          </div>
        ))}
        {/* 等級範囲の色分け表示 */}
        {gradeSettings.map((grade, index) => {
          const startPos = index === 0 ? 100 : gradeSettings[index - 1]?.gradeXStart ?? 100;
          const endPos = grade?.gradeXStart ?? 0;
          const width = startPos - endPos;

          if (width <= 0) return null;

          return (
            <div
              key={`range-${index}`}
              className={`grade-range grade-range--${index % 4}`}
              style={{
                left: `${endPos}%`,
                width: `${width}%`
              }}
            >
              <span className="grade-range__label">{grade?.gradeLetter || t('gradeAssignment.grade')}</span>
            </div>
          );
        })}
      </div>

      <div className="grade-boundary-instructions">
        <p>💡 {t('gradeAssignment.dragBoundaryHint')}</p>        <div className="grade-boundary-legend">
          {getBoundaryPairs().map((boundary) => (
            <span key={boundary.index} className="grade-boundary-legend-item">
              <strong>{t('gradeAssignment.boundaryLabel', { upper: boundary.upperGrade?.gradeLetter || t('gradeAssignment.grade'), lower: boundary.lowerGrade?.gradeLetter || t('gradeAssignment.grade') })}:</strong> {t('gradeAssignment.percentAbove', { percent: Math.round(boundary.position), grade: boundary.upperGrade?.gradeLetter || t('gradeAssignment.grade') })}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};